import { useTheme } from '@mui/material';
import type { NodeViewProps } from '@tiptap/react';
import { EditorContent, NodeViewWrapper, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
    Denominator,
    FractionContainer,
    Numerator,
} from 'components/FormulaRenderer/formulas/FormulaParts';
import { useFieldMentionExtension } from 'hooks/FieldMention/useFieldMentionExtension';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const EXTENSIONS = [
    StarterKit.configure({ heading: false, blockquote: false }),
];

function FractionSlot({
    content,
    isEditable,
    onUpdate,
    placeholder,
}: {
    content: string;
    isEditable: boolean;
    onUpdate: (html: string) => void;
    placeholder: string;
}) {
    const fieldMentionExtension = useFieldMentionExtension();
    const slotEditor = useEditor({
        editable: isEditable,
        extensions: [...EXTENSIONS, fieldMentionExtension],
        content: content || '',
        onUpdate: ({ editor: e }) => onUpdate(e.getHTML()),
    });
    const theme = useTheme();

    useEffect(() => {
        slotEditor?.setEditable(isEditable);
    }, [isEditable, slotEditor]);

    return (
        <div
            data-placeholder={content ? '' : placeholder}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <EditorContent
                style={{
                    backgroundColor: content.replace(/<[^>]*>/g, '').trim()
                        ? 'transparent'
                        : theme.palette.background.default,
                    borderRadius: 2,
                }}
                editor={slotEditor}
            />
        </div>
    );
}

const FractionNodeView = ({
    node,
    updateAttributes,
    selected,
    editor,
}: NodeViewProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { numerator, denominator } = node.attrs;
    return (
        <NodeViewWrapper
            as="span"
            style={{ display: 'inline-flex', verticalAlign: 'middle' }}
        >
            <FractionContainer
                style={{
                    margin: 0,
                    outline: selected
                        ? `2px solid ${theme.palette.primary.main}`
                        : 'none',
                    borderRadius: 2,
                }}
            >
                <Numerator>
                    <FractionSlot
                        content={numerator}
                        isEditable={editor.isEditable}
                        onUpdate={(html) =>
                            updateAttributes({ numerator: html })
                        }
                        placeholder={t('dashboardHeader.fractionNumerator')}
                    />
                </Numerator>
                <Denominator>
                    <FractionSlot
                        content={denominator}
                        isEditable={editor.isEditable}
                        onUpdate={(html) =>
                            updateAttributes({ denominator: html })
                        }
                        placeholder={t('dashboardHeader.fractionDenominator')}
                    />
                </Denominator>
            </FractionContainer>
        </NodeViewWrapper>
    );
};

export default FractionNodeView;
